/* Copyright (c) 2017 Backpack Health LLC */
/* global IS_ANDROID_BUILD, window */

import away from 'away';

import AppUtils from './AppUtils';
import { StorageKeys, lastActiveTime } from '../utilities/LocalStorage';
import { actions as sessionActions } from '../app/redux/ducks/session';

let idleTimer;

function logout() {
  AppUtils.logout({}, AppUtils.reloadApp);
}

function getMillisecondsToCallRefresh(session) {
  return session.getMillisecsUntilRefresh() * 0.7;
}

function getNowTime() {
  return new Date().getTime();
}

// REFRESH SESSION TOKEN TIMER
// =========================

function clearRefreshTokenTimer() {
  if (window.REFRESH_TOKEN_TIMER) {
    clearTimeout(window.REFRESH_TOKEN_TIMER);
    window.REFRESH_TOKEN_TIMER = null;
  }
}

function startRefreshTokenTimer(refreshIntervalTime, store) {
  clearRefreshTokenTimer();

  window.REFRESH_TOKEN_TIMER = setTimeout(() => {
    refreshToken(store);
  }, refreshIntervalTime);
}

function refreshToken(store) {
  store.dispatch(sessionActions.refreshToken((apiError, session) => {
    const millisecondsUntilExpire = getMillisecondsToCallRefresh(session);
    clearRefreshTokenTimer();

    if (apiError) {
      logout();
    } else if (millisecondsUntilExpire <= 0) {
      logout();
    } else {
      startRefreshTokenTimer(millisecondsUntilExpire, store);
    }
  }));
}

// IDLE TIMER
// =========================

function checkIfStillIdle(durationMilliseconds) {
  const nowTime = getNowTime();
  const lastActive = lastActiveTime.get() || 0;
  return nowTime - lastActive >= durationMilliseconds;
}

function startIdleTimer(durationMilliseconds, onIdle) {
  if (checkIfStillIdle(durationMilliseconds)) {
    onIdle();
    return () => {};
  }
  lastActiveTime.set(getNowTime());

  let checkAfterIdle;

  if (idleTimer) {
    // idleTimer.stop();
  } else {
    idleTimer = away(durationMilliseconds);
  }

  idleTimer.on('idle', () => {
    if (checkIfStillIdle(durationMilliseconds)) {
      onIdle();
    } else {
      checkAfterIdle = window.setInterval(() => {
        if (checkIfStillIdle(durationMilliseconds)) {
          window.clearInterval(checkAfterIdle);
          checkAfterIdle = null;
          onIdle();
        }
      }, 1000);
    }
  });

  function mouseMoveListener() {
    if (checkAfterIdle) {
      window.clearInterval(checkAfterIdle);
      checkAfterIdle = null;
    }

    if (checkIfStillIdle(durationMilliseconds)) {
      onIdle();
    } else {
      lastActiveTime.set(getNowTime());
    }
  }
  window.addEventListener('mousemove', mouseMoveListener);

  window.cancelIdleTimer = () => {
    window.removeEventListener('mousemove', mouseMoveListener);
    idleTimer.stop();
    window.cancelIdleTimer = null;
  };

  window.LOGOUT_STORAGE_EVENT_HANDLER = (event) => {
    if (event.key === StorageKeys.LOGOUT_EVENT) {
      logout();
    }
  };
  window.addEventListener('storage', window.LOGOUT_STORAGE_EVENT_HANDLER);

  return cancelIdleTimer;
}

function start(session, store) {
  const millisecondsUntilExpire = getMillisecondsToCallRefresh(session);
  const maxPossibleInterval = Math.pow(2, 31) - 1;
  const refreshIntervalTime = Math.min(millisecondsUntilExpire, maxPossibleInterval);
  startRefreshTokenTimer(refreshIntervalTime, store);

  if (!session.isTrusted() && !IS_ANDROID_BUILD) {
    // init the last active time to now
    lastActiveTime.set(getNowTime());
    startIdleTimer(session.getMillisecsDuration(), logout);
  }
}

function startPublicIdleTimer(onIdle) {
  // init the last active time to now
  lastActiveTime.set(getNowTime());
  const fifteenMinutes = 1000 * 60 * 15;
  return startIdleTimer(fifteenMinutes, onIdle);
}

function cancelIdleTimer() {
  if (window.cancelIdleTimer) {
    window.cancelIdleTimer();
  }
}

const ManageSession = {
  start,
  startPublicIdleTimer,
  cancelIdleTimer,
  clearRefreshTokenTimer,
};

export default ManageSession;
